import React from 'react';
import { Briefcase, FolderOpen } from 'lucide-react';
import type { CustomerRecord } from '../CustomerLogin';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { PortalLexcoreSnapshotBadge } from './PortalLexcoreSnapshotBadge';
import { formatDocumentCount, resolveArbeitskontextLabel } from './portalArbeitskontext';

export interface PortalArbeitskontextBarProps {
  customer: Pick<CustomerRecord, 'kanzlei' | 'tier'>;
  documentCount?: number | null;
  placement?: 'sidebar-footer' | 'content';
  className?: string;
}

/** Kompakter Arbeitskontext: Kanzlei, Tarif, Rechtsstand, Dokumentenzähler. */
export const PortalArbeitskontextBar: React.FC<PortalArbeitskontextBarProps> = ({
  customer,
  documentCount,
  placement = 'content',
  className = '',
}) => {
  const arbeitskontext = resolveArbeitskontextLabel(customer);
  const docsLabel = formatDocumentCount(documentCount);
  const isSidebar = placement === 'sidebar-footer';

  const kanzleiBadge = (
    <PortalBadge
      label={customer.kanzlei}
      accent="blue"
      icon={<Briefcase className="w-2.5 h-2.5" aria-hidden />}
      className={`!px-2 !py-0.5 !text-[10px] ${isSidebar ? 'max-w-full' : 'max-w-[200px]'} truncate`}
    />
  );

  const arbeitskontextBadge = arbeitskontext ? (
    <PortalBadge
      label={`Arbeitskontext: ${arbeitskontext}`}
      accent="violet"
      className={`!px-2 !py-0.5 !text-[10px] ${isSidebar ? 'max-w-full' : 'max-w-[220px]'} truncate`}
    />
  ) : null;

  const docsLine = (
    <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 font-medium tracking-wide min-w-0">
      <FolderOpen className="w-3 h-3 flex-shrink-0" aria-hidden />
      <span className="truncate">Kanzlei-Arbeitsplatz</span>
      <span className="text-slate-600 flex-shrink-0" aria-hidden>
        ·
      </span>
      <span className="truncate">{docsLabel}</span>
    </span>
  );

  if (isSidebar) {
    return (
      <div className={`flex flex-col gap-1.5 px-2 py-2 ${className}`} aria-label="Arbeitskontext">
        <div className="flex flex-wrap gap-1.5">
          {kanzleiBadge}
          {arbeitskontextBadge}
        </div>
        <PortalLexcoreSnapshotBadge size="xs" interactive />
        {docsLine}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 px-1 py-2 mb-3 border-b border-white/[0.06] ${className}`}
      aria-label="Arbeitskontext"
    >
      {kanzleiBadge}
      {arbeitskontextBadge}
      <PortalLexcoreSnapshotBadge size="xs" interactive />
      {docsLine}
    </div>
  );
};
